import {
  buildGrowthSeries,
  buildYoYGrowthSeries,
  findRowByYearMonth,
  getIncompleteMonthInfo,
  getRowMonthKey,
  getYearMonthForRow,
  IncompleteMonthInfo,
  MARKET_PULSE_TO_ROW_GROWTH,
  sliceClubDisplayWindow,
  sortRowsChronologically,
} from '../../utils/clubGrowthMetrics';
import { METRICS, MetricKey, MARKET_PULSE_YOY_MIN_MONTHS, PulseRow } from './marketPulseConstants';
import { formatPulseMonthLabel, rowKey } from './marketPulseFormatting';

export interface MarketYoYPulseRow extends PulseRow {
  salesGrowthYoY?: number | null;
  invoiceGrowthYoY?: number | null;
  priceGrowthYoY?: number | null;
  basketGrowthYoY?: number | null;
  pairedBranchCount?: number;
}

const MARKET_YOY_GROWTH_FIELD: Record<MetricKey, keyof MarketYoYPulseRow> = {
  salesGrowth: 'salesGrowthYoY',
  invoiceGrowth: 'invoiceGrowthYoY',
  priceGrowth: 'priceGrowthYoY',
  basketGrowth: 'basketGrowthYoY',
};

function buildMarketYoYGrowthSeries(marketYoYAll: MarketYoYPulseRow[]): Record<MetricKey, Map<string, number | null>> {
  const series = {} as Record<MetricKey, Map<string, number | null>>;
  METRICS.forEach((m) => {
    const map = new Map<string, number | null>();
    marketYoYAll.forEach((row) => {
      const field = MARKET_YOY_GROWTH_FIELD[m.key];
      const value = row[field];
      map.set(rowKey(row), typeof value === 'number' && Number.isFinite(value) ? value : null);
    });
    series[m.key] = map;
  });
  return series;
}

/** Strict market data often omits the in-progress month; merge relaxed partial rows when extrapolating. */
export function mergePartialMarketRows(
  strictMarket: PulseRow[],
  partialMarket: PulseRow[],
): PulseRow[] {
  if (!partialMarket.length) return strictMarket;
  const byKey = new Map(strictMarket.map((row) => [getRowMonthKey(row), row]));
  for (const row of partialMarket) {
    const key = getRowMonthKey(row);
    if (!byKey.has(key)) {
      byKey.set(key, row);
    }
  }
  return sortRowsChronologically([...byKey.values()]);
}

/** آیا دادهٔ کافی برای نمایش جدول YoY وجود دارد؟ (حداقل ۱۳ ماه + حداقل یک ردیف با رشد) */
export function hasMarketPulseYoYData(
  branchAll: PulseRow[],
  comparisonData?: MarketPulseComparisonData | null,
): boolean {
  if (sortRowsChronologically(branchAll).length < MARKET_PULSE_YOY_MIN_MONTHS) {
    return false;
  }
  if (!comparisonData?.tableRows.length) {
    return false;
  }
  return comparisonData.tableRows.some((row) =>
    METRICS.some((m) => {
      const cell = row.cells[m.key];
      return cell.branch !== null || cell.market !== null;
    }),
  );
}

export interface MarketPulseComparisonCell {
  branch: number | null;
  market: number | null;
  diff: number | null;
}

export interface MarketPulseComparisonRow {
  key: string;
  label: string;
  cells: Record<MetricKey, MarketPulseComparisonCell>;
  monthInfo: IncompleteMonthInfo;
  isIncomplete: boolean;
  isExtrapolated: boolean;
  branchGrowthUnreliable: boolean;
  /** Green check: current (and for YoY, prior-year) month must be complete and present. */
  showCompleteCheck: boolean;
}

export interface MarketPulseComparisonData {
  chartRows: MarketPulseComparisonRow[];
  tableRows: MarketPulseComparisonRow[];
  hasExtrapolatedMonths: boolean;
  hasIncompleteMonths: boolean;
  extrapolate: boolean;
}

export function computeMarketPulseComparisonData(
  branchAll: PulseRow[],
  marketAll: PulseRow[],
  baseMonths: number,
  extrapolate: boolean,
  marketPartialLatest: PulseRow[] = [],
): MarketPulseComparisonData {
  const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
  const effectiveMarketAll = extrapolate
    ? mergePartialMarketRows(marketAll, marketPartialLatest)
    : marketAll;

  const branchSeries = {} as Record<MetricKey, Map<string, number | null>>;
  const marketSeries = {} as Record<MetricKey, Map<string, number | null>>;
  METRICS.forEach((m) => {
    const growthType = MARKET_PULSE_TO_ROW_GROWTH[m.key];
    branchSeries[m.key] = buildGrowthSeries(branchAll, growthType, extrapolate);
    marketSeries[m.key] = buildGrowthSeries(effectiveMarketAll, growthType, extrapolate);
  });

  const chartRows = branchWindow.map((d, index) => {
    const key = rowKey(d);
    const monthInfo = getIncompleteMonthInfo(d, branchAll);
    const prevRow = index > 0 ? branchWindow[index - 1] : null;
    const prevMonthInfo = prevRow ? getIncompleteMonthInfo(prevRow, branchAll) : null;
    const branchGrowthUnreliable =
      monthInfo.incomplete || (prevMonthInfo?.incomplete ?? false);
    const cells = {} as Record<MetricKey, MarketPulseComparisonCell>;
    METRICS.forEach((m) => {
      const branch = branchSeries[m.key].get(key) ?? null;
      const market = marketSeries[m.key].get(key) ?? null;
      const diff = branch !== null && market !== null ? branch - market : null;
      cells[m.key] = { branch, market, diff };
    });
    return {
      key,
      label: formatPulseMonthLabel(d),
      cells,
      monthInfo,
      isIncomplete: monthInfo.incomplete,
      isExtrapolated: extrapolate && monthInfo.incomplete,
      branchGrowthUnreliable,
      showCompleteCheck: !monthInfo.incomplete,
    };
  });

  const tableRows = [...chartRows].reverse();
  const hasExtrapolatedMonths = chartRows.some((row) => row.isExtrapolated);
  const hasIncompleteMonths = chartRows.some((row) => row.isIncomplete);

  return {
    chartRows,
    tableRows,
    hasExtrapolatedMonths,
    hasIncompleteMonths,
    extrapolate,
  };
}

export function computeMarketPulseYoYComparisonData(
  branchAll: PulseRow[],
  marketYoYAll: MarketYoYPulseRow[],
  baseMonths: number,
  extrapolate: boolean,
  marketPartialLatest: PulseRow[] = [],
  marketAll: PulseRow[] = [],
): MarketPulseComparisonData {
  const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
  const effectiveMarketMoM = extrapolate
    ? mergePartialMarketRows(marketAll, marketPartialLatest)
    : marketAll;

  const branchSeries = {} as Record<MetricKey, Map<string, number | null>>;
  METRICS.forEach((m) => {
    const growthType = MARKET_PULSE_TO_ROW_GROWTH[m.key];
    branchSeries[m.key] = buildYoYGrowthSeries(branchAll, growthType, extrapolate);
  });
  const marketSeries = extrapolate
    ? (() => {
        const series = {} as Record<MetricKey, Map<string, number | null>>;
        METRICS.forEach((m) => {
          const growthType = MARKET_PULSE_TO_ROW_GROWTH[m.key];
          series[m.key] = buildYoYGrowthSeries(effectiveMarketMoM, growthType, extrapolate);
        });
        return series;
      })()
    : buildMarketYoYGrowthSeries(marketYoYAll);

  const chartRows = branchWindow.map((d) => {
    const key = rowKey(d);
    const monthInfo = getIncompleteMonthInfo(d, branchAll);
    const rowYm = getYearMonthForRow(d);
    const prevYearRow = findRowByYearMonth(branchAll, rowYm.year - 1, rowYm.month);
    const prevYearMonthInfo = prevYearRow ? getIncompleteMonthInfo(prevYearRow, branchAll) : null;
    const branchGrowthUnreliable =
      monthInfo.incomplete || (prevYearMonthInfo?.incomplete ?? false);
    // Green check only when both this month and the prior-year counterpart are complete.
    const showCompleteCheck =
      !monthInfo.incomplete && !!prevYearRow && !(prevYearMonthInfo?.incomplete ?? true);
    const cells = {} as Record<MetricKey, MarketPulseComparisonCell>;
    METRICS.forEach((m) => {
      const branch = branchSeries[m.key].get(key) ?? null;
      const market = marketSeries[m.key].get(key) ?? null;
      const diff = branch !== null && market !== null ? branch - market : null;
      cells[m.key] = { branch, market, diff };
    });
    return {
      key,
      label: formatPulseMonthLabel(d),
      cells,
      monthInfo,
      isIncomplete: monthInfo.incomplete,
      isExtrapolated: extrapolate && monthInfo.incomplete,
      branchGrowthUnreliable,
      showCompleteCheck,
    };
  });

  const tableRows = [...chartRows].reverse();
  const hasExtrapolatedMonths = chartRows.some((row) => row.isExtrapolated);
  const hasIncompleteMonths = chartRows.some((row) => row.isIncomplete);

  return {
    chartRows,
    tableRows,
    hasExtrapolatedMonths,
    hasIncompleteMonths,
    extrapolate,
  };
}
